import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Send, Sparkles } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";


const convo: { msg: string; rep: string }[] = [
  {
    msg: "I feel alive again, and the life I'm building is already on its way to me.",
    rep: "Received. Now go feel it like it's already done. ✨",
  },
  {
    msg: "Thank you for the calm, the love, and the certainty I already feel.",
    rep: "Always. Keep feeling it, the rest is handled. 🤍",
  },
  {
    msg: "I am becoming the person I can feel myself to be.",
    rep: "You already are. I'm just catching the world up. ✦",
  },
  {
    msg: "I feel it now, and somehow I know it's already done.",
    rep: "It is. Consider it in motion. 🌙",
  },
];

const HeroDM: React.FC = () => {
  const [sent, setSent] = useState("");
  const [reply, setReply] = useState<string | "typing" | "">("");
  const [caretOn, setCaretOn] = useState(false);
  const [metaShow, setMetaShow] = useState(false);
  const [replyShow, setReplyShow] = useState(false);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setSent(convo[0].msg);
      setReply(convo[0].rep);
      setMetaShow(true);
      setReplyShow(true);
      return;
    }
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void) =>
      timeouts.push(setTimeout(fn, ms));

    let idx = 0;
    let cancelled = false;

    setSent("");
    setReply("");
    setMetaShow(false);
    setReplyShow(false);

    const typeMsg = (text: string, done: () => void) => {
      setCaretOn(true);
      let i = 0;
      const step = () => {
        if (cancelled) return;
        i++;
        setSent(text.slice(0, i));
        if (i < text.length) {
          at(44 + Math.random() * 40, step);
        } else {
          setCaretOn(false);
          done();
        }
      };
      step();
    };

    const cycle = () => {
      if (cancelled) return;
      const item = convo[idx];
      setSent("");
      setReply("");
      setMetaShow(false);
      setReplyShow(false);
      typeMsg(item.msg, () => {
        at(380, () => setMetaShow(true));
        at(950, () => {
          setReply("typing");
          setReplyShow(true);
        });
        at(2100, () => setReply(item.rep));
        at(5600, () => {
          setMetaShow(false);
          setReplyShow(false);
        });
        at(6100, () => {
          idx = (idx + 1) % convo.length;
          at(350, cycle);
        });
      });
    };

    at(700, cycle);

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, []);

  return (
    <section
      id="top"
      className="relative min-h-[100svh] flex items-center pt-32 pb-20 overflow-hidden"
    >
      <div className="absolute inset-0 z-[-1] bg-cosmic-radial opacity-50" />

      <div className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        {/* Copy */}
        <div className="space-y-8 max-w-xl order-1">
          <ScrollReveal direction="left" delay={200}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wide text-cosmic-200 border border-cosmic-400/30 bg-cosmic-900/50 backdrop-blur-sm">
              The universe is online
            </span>
          </ScrollReveal>

          <ScrollReveal direction="left" delay={400}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold leading-tight">
              <span className="text-white">Feel it first. </span>
              <span className="text-cosmic-gradient">Watch it reply.</span>
            </h1>
          </ScrollReveal>

          <ScrollReveal direction="left" delay={600}>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Somewhere along the way the spark went quiet, and we started waiting to feel
              alive <i>once</i> life looks the way we want. Mindmail turns that around, a daily
              practice for feeling it <b className="text-white">now</b>, so reality has something
              to catch up to.
            </p>
          </ScrollReveal>

          <ScrollReveal direction="left" delay={800}>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a href="#pricing" className="cosmic-button text-center flex items-center justify-center">
                Send your first DM
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a href="#idea" className="cosmic-button-outline text-center flex items-center justify-center">
                Why it works
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="left" delay={1000}>
            <p className="text-white/50 text-sm">
              From $14.99/month <span className="opacity-50 mx-2">·</span> Cancel anytime
              <span className="opacity-50 mx-2">·</span> Secure checkout via Stripe
            </p>
          </ScrollReveal>
        </div>

        {/* DM composer mock */}
        <div className="relative flex justify-center items-center order-2">
          <div className="absolute inset-0 bg-cosmic-radial opacity-40 blur-2xl" />
          <div className="relative w-[360px] md:w-[420px] lg:w-[480px] cosmic-card overflow-hidden">
              <div className="absolute top-0 right-0 -translate-x-1/2 -translate-y-1/4 w-12 h-12 rounded-full bg-cosmic-300/30 animate-float" />
              <div className="absolute bottom-0 left-0 translate-x-1/2 translate-y-1/4 w-16 h-16 rounded-full bg-cosmic-400/20 animate-float" />

              <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
                <div className="w-10 h-10 rounded-full bg-cosmic-500/30 border border-cosmic-400/30 flex items-center justify-center text-white">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">The Universe</div>
                  <div className="text-xs text-cosmic-200 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cosmic-300 animate-pulse" />
                    always online
                  </div>
                </div>
              </div>

              <div className="px-5 py-5 flex flex-col gap-3 relative min-h-[210px]">
                <div className="self-end max-w-[85%] rounded-2xl rounded-br-sm px-4 py-3 bg-cosmic-500/30 border border-cosmic-400/30 text-white text-[15px] italic leading-snug">
                  <span>{sent}</span>
                  <span
                    aria-hidden="true"
                    className={`inline-block w-[2px] h-[1em] align-[-2px] ml-0.5 bg-white/80 transition-opacity ${
                      caretOn ? "animate-pulse opacity-100" : "opacity-0"
                    }`}
                  />
                </div>
                <div
                  className={`self-end text-[11px] text-white/40 -mt-1 transition-opacity duration-300 ${
                    metaShow ? "opacity-100" : "opacity-0"
                  }`}
                >
                  Delivered · just now
                </div>
                <div
                  className={`self-start max-w-[85%] rounded-2xl rounded-bl-sm px-4 py-3 bg-white/5 border border-white/10 text-white/90 text-[15px] leading-snug transition-opacity duration-300 ${
                    replyShow ? "opacity-100" : "opacity-0"
                  }`}
                >
                  {reply === "typing" ? (
                    <span className="inline-flex items-center gap-1.5 py-1">
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce"
                        style={{ animationDelay: "180ms" }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce"
                        style={{ animationDelay: "360ms" }}
                      />
                    </span>
                  ) : (
                    reply
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 px-3 py-3 border-t border-white/5 bg-white/[0.02]">
                <div className="flex-1 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/40">
                  Message the universe…
                </div>
                <div className="w-9 h-9 rounded-full bg-cosmic-500 grid place-items-center text-white">
                  <Send className="h-4 w-4" />
                </div>
              </div>
            </div>
        </div>
      </div>
    </section>
  );
};

export default HeroDM;
